<template>
  <main class="container mx-auto px-4 py-8 max-w-4xl">
    <header class="mb-6">
      <h1 class="text-2xl font-semibold">{{ channelTitle || 'Kanal Özeti' }}</h1>
      <p v-if="channelId" class="text-gray-600 mt-1">Kanal ID: {{ channelId }}</p>
    </header>

    <section v-if="videos.length">
      <ul class="space-y-5">
        <li v-for="v in videos" :key="v.video_id" class="border-b pb-4">
          <RouterLink :to="createVideoUrl(v.title || '', v.video_id, preferredLang)" class="text-lg font-medium underline">
            {{ v.title || v.video_id }}
          </RouterLink>
          <div class="text-sm text-gray-500 mt-1">
            {{ new Date(v.published_at).toLocaleString() }} • {{ v.language?.toUpperCase() || preferredLang.toUpperCase() }}
          </div>
          <p v-if="v.excerpt" class="text-gray-700 mt-2">{{ v.excerpt }}</p>
        </li>
      </ul>
    </section>
    <section v-else class="text-gray-600">
      Bu kanal için henüz özetli video bulunamadı.
    </section>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import api from '../services/apiService';
import { createVideoUrl } from '../utils/helpers';

const route = useRoute();
const channelId = ref<string>('');
const channelSlug = ref<string>('');
const channelTitle = ref<string>('');
const preferredLang = ref<string>('tr');
const videos = ref<Array<{ video_id: string; title?: string; published_at?: string; language?: string; excerpt?: string }>>([]);
const publicBase = (import.meta as any).env?.VITE_PUBLIC_BASE_URL || window.location.origin;

// Kanal ID'yi route parametrelerinden çıkar
function extractChannelId(): string {
  // Eğer SEO-friendly URL kullanılıyorsa (channelSlug/channelId formatı)
  if (route.params.channelId && route.params.channelSlug) {
    return route.params.channelId as string;
  }
  
  // Eğer sadece channelId varsa
  if (route.params.channelId) {
    return route.params.channelId as string;
  }
  
  return '';
}

// Kanal slug'ını route parametrelerinden çıkar
function extractChannelSlug(): string {
  if (route.params.channelSlug) {
    return route.params.channelSlug as string;
  }
  return '';
}

// Kanal isminden slug oluştur
function createChannelSlug(title: string): string {
  if (!title) return '';
  
  return title
    .toLowerCase()
    // Türkçe karakterleri İngilizce karşılıklarına çevir
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '') // Diğer özel karakterleri kaldır
    .replace(/\s+/g, '-') // Boşlukları tire ile değiştir
    .replace(/-+/g, '-') // Çoklu tireleri tek tire yap
    .trim();
}

// URL'yi SEO-friendly yap
function updateUrlToSeoFriendly(channelId: string, title: string) {
  if (!title || !channelId) return;
  
  const seoSlug = createChannelSlug(title);
  const currentPath = route.path;
  const newPath = `/channel/${seoSlug}/${channelId}`;
  
  // Eğer URL zaten SEO-friendly değilse, güncelle
  if (currentPath !== newPath && !currentPath.includes(seoSlug)) {
    window.history.replaceState({}, '', newPath);
  }
}

function setMeta(title: string, description: string) {
  document.title = title;
  const metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (metaDesc) {
    metaDesc.setAttribute('content', description);
  } else {
    const m = document.createElement('meta');
    m.setAttribute('name', 'description');
    m.setAttribute('content', description);
    document.head.appendChild(m);
  }
}

function setMetaTag(attr: 'name' | 'property', key: string, content: string) {
  const existing = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  const tag = existing || document.createElement('meta');
  tag.setAttribute(attr, key);
  tag.setAttribute('content', content);
  if (!existing) document.head.appendChild(tag);
}

function setLinkTag(rel: string, href: string, attrs: Record<string, string> = {}) {
  const existing = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  const tag = existing || document.createElement('link');
  tag.setAttribute('rel', rel);
  tag.setAttribute('href', href);
  Object.entries(attrs).forEach(([key, value]) => tag.setAttribute(key, value));
  if (!existing) document.head.appendChild(tag);
}

async function fetchChannel() {
  channelId.value = extractChannelId();
  channelSlug.value = extractChannelSlug();
  
  if (!channelId.value) return;
  
  try {
    // Kanala ait son videolar + özetler için basit bir API kombinasyonu
    const res = await api.get(`/api/public/channel/${channelId.value}`);
    channelTitle.value = res?.title || '';
    
    // URL'yi SEO-friendly yap
    updateUrlToSeoFriendly(channelId.value, channelTitle.value);
  } catch {}

  // Son 20 video için basit liste (channel_videos + videos birleştirilmiş endpoint yoksa fallback)
  try {
    const list = await api.get(`/api/public/channel/${channelId.value}`);
    const items: any[] = list?.videos || [];
    videos.value = items.map(v => ({
      video_id: v.video_id,
      title: v.title,
      published_at: v.published_at,
      language: v.language,
      excerpt: v.excerpt
    }));
  } catch {}

  setMeta(`${channelTitle.value || 'Kanal'} — Özetler | Veciz`, `${channelTitle.value} kanalı için özetler ve sesli okumalar.`);
  // Canonical & basic OG for channel page
  const canonical = `${publicBase}/channel/${channelId.value}`;
  setLinkTag('canonical', canonical);
  setMetaTag('property', 'og:title', channelTitle.value || 'Kanal Özeti');
  setMetaTag('property', 'og:description', `${channelTitle.value} kanalı için özetler ve sesli okumalar.`);
  setMetaTag('property', 'og:type', 'website');
  setMetaTag('property', 'og:url', canonical);
  setMetaTag('name', 'twitter:card', 'summary');
  setMetaTag('name', 'twitter:title', channelTitle.value || 'Kanal Özeti');
  setMetaTag('name', 'twitter:description', `${channelTitle.value} kanalı için özetler ve sesli okumalar.`);
  
  // Add structured data for channel page
  addChannelStructuredData();
}

function addChannelStructuredData() {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": channelTitle.value || "YouTube Channel",
    "description": `${channelTitle.value} kanalı için özetler ve sesli okumalar.`,
    "url": `${publicBase}/channel/${channelId.value}`,
    "publisher": {
      "@type": "Organization",
      "name": "Veciz AI",
      "url": publicBase
    },
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": videos.value.length,
      "itemListElement": videos.value.map((video, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "VideoObject",
          "name": video.title || `Video ${video.video_id}`,
          "url": `${publicBase}/video/${video.video_id}/${video.language || 'tr'}`,
          "thumbnailUrl": `https://i.ytimg.com/vi/${video.video_id}/maxresdefault.jpg`,
          "uploadDate": video.published_at
        }
      }))
    }
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
}

onMounted(fetchChannel);
</script>

<style scoped>
.container { max-width: 960px; }
</style>


